import React, {
  useState,
  useEffect,
  Fragment,
  ChangeEvent,
  useRef,
  useMemo,
  useCallback,
} from "react";
import {
  Building2,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Folder,
  Download,
  Loader2,
  Plus,
  RefreshCw,
  Upload,
  User,
} from "lucide-react";
import moment from "moment";
import { useAuth0 } from "@auth0/auth0-react";
import { safeGetAccessToken } from "../utils/auth";
import { toast } from "react-toastify";
import { API_BASE, TOASTPROPS } from "../shared/Constants";
import { reportHandledError, reportApiError } from "../utils/reportError";
import { OrganizationSummary } from "../shared/Interfaces";
import { OrganizationPermission, hasPermission } from "../shared/organizationPermissions";

import { Button } from "./ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Skeleton } from "./ui/skeleton";
import { cn } from "../lib/utils";
import { formatDecimalBR } from "../utils/currency";

interface FileObject {
  id: string;
  bytes: number;
  created_at: number;
  expires_at?: number;
  filename: string;
  object: string;
  purpose: string;
  status: string;
  status_details?: string;
  __manuallyAdded?: boolean;
}

interface ListOfFilesResponse {
  data: FileObject[];
  has_more: boolean;
  last_id: string | null;
}

interface DownloadInfo {
  percent: number;
  received: number;
  total: number | null;
}

const fmtBytes = (b: number) =>
  b >= 1024 * 1024
    ? `${formatDecimalBR(b / 1024 / 1024)} MB`
    : `${formatDecimalBR(b / 1024)} KB`;

const secondsToDate = (sec: number) =>
  new Date(sec * 1000).toLocaleString("pt-BR");

const getStatusInfo = (status: string): { color: string; label: string } => {
  switch (status) {
    case "processed":
      return { color: "bg-success", label: "Processado" };
    case "processing":
      return { color: "bg-blue-500", label: "Em processamento" };
    case "uploading":
      return { color: "bg-warning", label: "Enviando" };
    case "error":
      return { color: "bg-destructive", label: "Erro" };
    case "expired":
      return { color: "bg-destructive", label: "Expirado" };
    case "validating":
      return { color: "bg-warning", label: "Validando" };
    default:
      return { color: "bg-muted-foreground", label: status };
  }
};

export default function Files() {
  const { getAccessTokenSilently, loginWithRedirect, user } = useAuth0();

  const [files, setFiles] = useState<FileObject[]>([]);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [lastFileId, setLastFileId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [openUploadModal, setOpenUploadModal] = useState(false);
  const [sendingFile, setSendingFile] = useState(false);
  const [refreshToggle, setRefreshToggle] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState<Record<string, DownloadInfo>>({});
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const [billingScope, setBillingScope] = useState<"personal" | "organization">("personal");
  const [activeOrganizationId, setActiveOrganizationId] = useState<string>("");
  const [organizations, setOrganizations] = useState<OrganizationSummary[]>([]);
  const [loadingOrganizations, setLoadingOrganizations] = useState<boolean>(false);
  const [showAllOrgFiles, setShowAllOrgFiles] = useState<boolean>(false);

  const itemsPerPage = 5;

  const currentUserId = useMemo(() => {
    if (!user?.sub) return "";
    const parts = user.sub.split("|");
    return parts[parts.length - 1] || user.sub;
  }, [user]);

  const memberOrganizations = useMemo(
    () =>
      organizations.filter((org) =>
        Boolean(org.members?.some((member) => member.user_id === currentUserId))
      ),
    [organizations, currentUserId]
  );

  const organizationsWithApiAccess = useMemo(
    () =>
      memberOrganizations.filter((org) => {
        const m = org.members?.find((member) => member.user_id === currentUserId);
        if (!m) return false;
        return hasPermission(m.permissions, OrganizationPermission.API_KEYS_VIEW_ALL);
      }),
    [memberOrganizations, currentUserId]
  );

  const hasOrganizations = organizations.length > 0;
  const hasMemberOrganizations = memberOrganizations.length > 0;
  const hasOrganizationsWithApiAccess = organizationsWithApiAccess.length > 0;

  const currentOrgMembership = useMemo(() => {
    if (!activeOrganizationId) return undefined;
    const org = organizations.find((o) => o.organization_id === activeOrganizationId);
    return org?.members?.find((m) => m.user_id === currentUserId);
  }, [activeOrganizationId, organizations, currentUserId]);

  const canViewAllOrgFiles = Boolean(
    billingScope === "organization" &&
      currentOrgMembership &&
      hasPermission(currentOrgMembership.permissions, OrganizationPermission.BATCH_FILES_VIEW_ALL)
  );

  const fetchOrganizations = useCallback(async () => {
    try {
      setLoadingOrganizations(true);
      const access_token = await safeGetAccessToken({ getAccessTokenSilently, loginWithRedirect });
      if (!access_token) return;
      const response = await fetch(`${API_BASE}/api/organizations/list`, {
        method: "GET",
        headers: { Authorization: `Auth ${access_token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setOrganizations(data["organizations"] ?? []);
      } else {
        reportApiError(response, { feature: "files.organizations", method: "GET" });
      }
    } catch (error) {
      reportHandledError(error, { category: "api", feature: "files.organizations" });
      toast.error("Erro ao buscar organizações.", TOASTPROPS);
    } finally {
      setLoadingOrganizations(false);
    }
  }, [getAccessTokenSilently, loginWithRedirect]);

  useEffect(() => {
    fetchOrganizations();
  }, [fetchOrganizations]);

  useEffect(() => {
    if (billingScope === "organization") {
      if (!hasMemberOrganizations) {
        setBillingScope("personal");
        setActiveOrganizationId("");
      } else if (!memberOrganizations.some((org) => org.organization_id === activeOrganizationId)) {
        setActiveOrganizationId(memberOrganizations[0].organization_id);
      }
    }
  }, [billingScope, memberOrganizations, activeOrganizationId, hasMemberOrganizations]);

  useEffect(() => {
    if (billingScope !== "organization" || !activeOrganizationId || !canViewAllOrgFiles) {
      setShowAllOrgFiles(false);
    }
  }, [billingScope, activeOrganizationId, canViewAllOrgFiles]);

  const buildFileUrl = (
    path: string,
    params: Record<string, string> = {},
    options?: { includeOrganization?: boolean; viewAll?: boolean }
  ) => {
    const { includeOrganization = true, viewAll = false } = options ?? {};
    const search = new URLSearchParams(params);
    if (includeOrganization && billingScope === "organization" && activeOrganizationId) {
      search.set("organization_id", activeOrganizationId);
    }
    if (viewAll && billingScope === "organization" && activeOrganizationId) {
      search.set("view_all", "true");
    }
    const query = search.toString();
    return `${API_BASE}/api${path}${query ? `?${query}` : ""}`;
  };

  async function loadMoreFiles() {
    if (loading) return;
    try {
      setLoading(true);
      const accessToken = await safeGetAccessToken({ getAccessTokenSilently, loginWithRedirect });
      if (!accessToken) return;
      const paramsObj: { limit: string; after?: string; purpose?: string } = {
        limit: itemsPerPage.toString(),
        purpose: "batch",
      };
      if (lastFileId) paramsObj.after = lastFileId;

      const url = buildFileUrl("/files-platform", paramsObj, { viewAll: showAllOrgFiles });
      const res = await fetch(url, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Auth ${accessToken}`,
        },
      });
      if (!res.ok) {
        reportApiError(res, { feature: "files.list", method: "GET" });
        toast.error("Falha ao carregar mais arquivos", TOASTPROPS);
      }
      const result: ListOfFilesResponse = await res.json();
      setFiles((prev) => [...prev, ...result.data]);
      setHasMore(result.has_more ?? false);
      setLastFileId(result.last_id ?? null);
    } catch (err) {
      reportHandledError(err, { category: "api", feature: "files.list" });
      toast.error("Erro ao carregar mais arquivos: " + (err as Error).message, TOASTPROPS);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    async function fetchInitialFiles() {
      try {
        if (loading) return;
        setLoading(true);
        setFiles([]);
        setLastFileId(null);

        const accessToken = await safeGetAccessToken({ getAccessTokenSilently, loginWithRedirect });
        if (!accessToken) return;
        const paramsObj = { limit: itemsPerPage.toString(), purpose: "batch" };

        const url = buildFileUrl("/files-platform", paramsObj, { viewAll: showAllOrgFiles });
        const res = await fetch(url, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Auth ${accessToken}`,
          },
        });
        if (!res.ok) {
          reportApiError(res, { feature: "files.list", method: "GET" });
          toast.error("Falha ao carregar arquivos", TOASTPROPS);
        }
        const result: ListOfFilesResponse = await res.json();
        setFiles(result.data);
        setHasMore(result.has_more ?? false);
        setLastFileId(result.last_id ?? null);
      } catch (error) {
        reportHandledError(error, { category: "api", feature: "files.list" });
        toast.error("Erro ao carregar arquivos", TOASTPROPS);
      } finally {
        setLoading(false);
      }
    }
    fetchInitialFiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshToggle, billingScope, activeOrganizationId, showAllOrgFiles]);

  const handleDownload = async (fileId: string, filename: string) => {
    try {
      const accessToken = await safeGetAccessToken({ getAccessTokenSilently, loginWithRedirect });
      if (!accessToken) return;
      setDownloadProgress((p) => ({
        ...p,
        [fileId]: { percent: 0, received: 0, total: null },
      }));

      const downloadUrl = buildFileUrl(
        `/files-platform/${fileId}/content`,
        {},
        { viewAll: showAllOrgFiles }
      );
      const res = await fetch(downloadUrl, {
        headers: { Authorization: `Auth ${accessToken}` },
      });
      if (!res.ok) {
        reportApiError(res, { feature: "files.download", method: "GET" });
        throw new Error("Falha no download");
      }

      const total = +res.headers.get("Content-Length")!;
      const reader = res.body!.getReader();
      const chunks: Uint8Array[] = [];
      let received = 0;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        chunks.push(value!);
        received += value!.length;

        if (total) {
          setDownloadProgress((p) => ({
            ...p,
            [fileId]: { percent: Math.round((received / total) * 100), received, total },
          }));
        } else {
          setDownloadProgress((p) => ({
            ...p,
            [fileId]: { percent: 0, received, total: null },
          }));
        }
      }

      const blob = new Blob(chunks as BlobPart[]);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      reportHandledError(error, { category: "api", feature: "files.download" });
      toast.error("Ocorreu um erro ao baixar o arquivo", TOASTPROPS);
    } finally {
      setDownloadProgress((p) => {
        const info = p[fileId];
        return {
          ...p,
          [fileId]: {
            percent: 100,
            received: info?.total ?? info?.received ?? 0,
            total: info?.total ?? info?.received ?? 0,
          },
        };
      });
      setTimeout(
        () =>
          setDownloadProgress((p) => {
            const { [fileId]: _ignore, ...rest } = p;
            return rest;
          }),
        2000
      );
    }
  };

  const filteredFiles = files
    .filter((f): f is FileObject => !!f && typeof f.id === "string")
    .filter((f) => f.id.toLowerCase().includes(searchTerm.toLowerCase()));

  useEffect(() => {
    const trimmed = searchTerm.trim();
    if (trimmed.length === 36) {
      const exists = files.find((f) => f.id.toLowerCase() === trimmed.toLowerCase());
      if (!exists && filteredFiles.length === 0) {
        (async () => {
          try {
            const accessToken = await safeGetAccessToken({ getAccessTokenSilently, loginWithRedirect });
            if (!accessToken) return;
            const url = buildFileUrl(
              `/files-platform/${trimmed}`,
              {},
              { viewAll: showAllOrgFiles }
            );
            const res = await fetch(url, {
              headers: {
                "Content-Type": "application/json",
                Authorization: `Auth ${accessToken}`,
              },
            });
            if (!res.ok) {
              reportApiError(res, { feature: "files.fetch", method: "GET" });
              toast.error("Arquivo não encontrado", TOASTPROPS);
            }
            const data: FileObject = await res.json();
            setFiles((p) => [{ ...data, __manuallyAdded: true }, ...p]);
          } catch (error) {
            reportHandledError(error, { category: "api", feature: "files.fetch" });
            toast.error("Erro ao buscar arquivo", TOASTPROPS);
          }
        })();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, files, filteredFiles]);

  useEffect(() => {
    if (searchTerm.trim().length !== 36) {
      setFiles((prev) => prev.filter((f) => !f.__manuallyAdded));
    }
  }, [searchTerm]);

  const handleFileSelect = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length) setSelectedFile(e.target.files[0]);
  };

  const handleUploadFile = async () => {
    if (!selectedFile) {
      toast.error("Por favor, selecione um arquivo .jsonl antes de fazer o upload.", TOASTPROPS);
      return;
    }
    if (!selectedFile.name.toLowerCase().endsWith(".jsonl")) {
      toast.error("Somente arquivos .jsonl são permitidos.", TOASTPROPS);
      return;
    }
    if (selectedFile.size > 200 * 1024 * 1024) {
      toast.error("O arquivo deve ter no máximo 200MB.", TOASTPROPS);
      return;
    }
    if (sendingFile) return;

    try {
      setSendingFile(true);
      const accessToken = await safeGetAccessToken({ getAccessTokenSilently, loginWithRedirect });
      if (!accessToken) return;
      const fd = new FormData();
      fd.append("file", selectedFile);
      fd.append("purpose", "batch");

      const url = buildFileUrl("/files-platform");
      const res = await fetch(url, {
        method: "POST",
        headers: { Authorization: `Auth ${accessToken}` },
        body: fd,
      });

      const responseData = await res.json().catch(() => ({} as any));
      if (!res.ok) {
        reportApiError(res, { feature: "files.upload", method: "POST" });
        const errorMessage =
          (responseData as any).detail ||
          (responseData as any).error ||
          `Error: ${res.statusText}`;
        toast.error(errorMessage, TOASTPROPS);
        return;
      }

      const uploaded: FileObject = responseData as FileObject;
      setFiles((p) => [uploaded, ...p]);
      toast.success("Upload realizado com sucesso!", TOASTPROPS);
      setSelectedFile(null);
    } catch (err: any) {
      reportHandledError(err, { category: "upload", feature: "files.upload" });
      toast.error("Algo deu errado ao fazer o upload: " + err.message, TOASTPROPS);
    } finally {
      setSendingFile(false);
    }
  };

  const handleCloseModal = () => {
    setOpenUploadModal(false);
    setSelectedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => e.preventDefault();
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (e.dataTransfer.files.length) {
      setSelectedFile(e.dataTransfer.files[0]);
      e.dataTransfer.clearData();
    }
  };

  const toggleExpansion = (id: string) => {
    const next = new Set(expandedRows);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setExpandedRows(next);
  };

  const handleScopeChange = (value: string) => {
    if (value === "personal") {
      setBillingScope("personal");
      setActiveOrganizationId("");
    } else {
      setBillingScope("organization");
      setActiveOrganizationId(value);
    }
  };

  const scopeSelectValue =
    billingScope === "personal" ? "personal" : activeOrganizationId || "";
  const isOrganizationScope = billingScope === "organization";

  return (
    <div className="flex flex-col gap-6 pb-12">
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="text-[15px]">Gerenciamento de Arquivos</CardTitle>
          <CardDescription>
            Faça upload e gerencie seus arquivos JSONL para processamento em batch.
            Arquivos devem ter no máximo 200MB e conter até 50.000 requisições.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <a
            href="https://docs.maritaca.ai/pt/batch-api"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline underline-offset-4"
          >
            Guia de início rápido da batch API
            <ExternalLink className="h-3.5 w-3.5" />
          </a>

          {/* Scope controls */}
          <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
            <div className="flex w-full flex-col items-start gap-3 lg:w-[400px]">
              <Label className="text-sm text-muted-foreground">Gerenciar arquivos de:</Label>
              {loadingOrganizations ? (
                <Skeleton className="h-10 w-full rounded-lg" />
              ) : (
                <>
                  <Select value={scopeSelectValue || "personal"} onValueChange={handleScopeChange}>
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="personal">
                        <span className="flex items-center gap-2">
                          <User className="h-4 w-4" />
                          Minha conta pessoal
                        </span>
                      </SelectItem>
                      {memberOrganizations.map((org) => (
                        <SelectItem key={org.organization_id} value={org.organization_id}>
                          <span className="flex items-center gap-2">
                            <Building2 className="h-4 w-4" />
                            {org.name || org.organization_id}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {billingScope === "organization" && canViewAllOrgFiles && (
                    <Button
                      variant={showAllOrgFiles ? "default" : "outline"}
                      size="sm"
                      onClick={() => setShowAllOrgFiles((prev) => !prev)}
                    >
                      {showAllOrgFiles
                        ? "Ver Apenas Meus Arquivos"
                        : "Ver Todos os Arquivos da Org"}
                    </Button>
                  )}
                </>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={() => setRefreshToggle((p) => !p)}
                className="gap-2"
              >
                <RefreshCw className="h-4 w-4" />
                Atualizar
              </Button>
              <Button
                size="lg"
                onClick={() => setOpenUploadModal(true)}
                className="gap-2"
              >
                <Plus className="h-4 w-4" />
                Upload Arquivo
              </Button>
            </div>
          </div>

        </CardContent>
      </Card>

      <Card className="overflow-hidden">
        <div className="border-b border-border p-4">
          <Input
            placeholder="Buscar por File ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="max-w-md"
          />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1100px] table-fixed text-sm">
            <colgroup>{[24, 24, 12, 10, 14, 16].map((width, i) => <col key={i} style={{ width: width + "%" }} />)}</colgroup>
            <thead>
              <tr className="border-b border-border bg-muted/40 text-left text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                <th className="px-5 py-3">File ID</th>
                <th className="px-5 py-3">Nome do Arquivo</th>
                <th className="px-5 py-3">Status</th>
                <th className="px-5 py-3">Tamanho</th>
                <th className="px-5 py-3">Criado em</th>
                <th className="px-5 py-3 text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {loading && files.length === 0 ? (
                Array.from({ length: itemsPerPage }).map((_, i) => (
                  <tr key={i} className="border-b border-border/60">
                    <td className="px-5 py-4"><Skeleton className="max-w-full h-4 w-52" /></td>
                    <td className="px-5 py-4"><Skeleton className="max-w-full h-4 w-40" /></td>
                    <td className="px-5 py-4"><Skeleton className="max-w-full h-4 w-24" /></td>
                    <td className="px-5 py-4"><Skeleton className="max-w-full h-4 w-16" /></td>
                    <td className="px-5 py-4"><Skeleton className="max-w-full h-4 w-32" /></td>
                    <td className="px-5 py-4 text-right"><Skeleton className="max-w-full ml-auto h-8 w-24" /></td>
                  </tr>
                ))
              ) : filteredFiles.length === 0 && !loading ? null : (
                filteredFiles.map((file) => {
                  const isExpanded = expandedRows.has(file.id);
                  const prog = downloadProgress[file.id];
                  const info = getStatusInfo(file.status);

                  return (
                    <Fragment key={file.id}>
                      <tr className="border-b border-border/60 transition-colors hover:bg-muted/30">
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => toggleExpansion(file.id)}
                              className="rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                              aria-label={isExpanded ? "Recolher" : "Expandir"}
                            >
                              {isExpanded ? (
                                <ChevronDown className="h-4 w-4" />
                              ) : (
                                <ChevronRight className="h-4 w-4" />
                              )}
                            </button>
                            <code className="min-w-0 break-all rounded-md bg-muted px-2 py-1 font-mono text-xs text-foreground/80">
                              {file.id}
                            </code>
                          </div>
                        </td>
                        <td className="break-words px-5 py-4 text-foreground">{file.filename}</td>
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-2">
                            <span className={cn("h-2 w-2 rounded-full", info.color)} />
                            <span className="text-foreground">{info.label}</span>
                          </div>
                        </td>
                        <td className="px-5 py-4 text-muted-foreground">{fmtBytes(file.bytes)}</td>
                        <td className="px-5 py-4 text-muted-foreground">{secondsToDate(file.created_at)}</td>
                        <td className="px-5 py-4">
                          <div className="flex items-center justify-end gap-2">
                            {prog ? (
                              <div className="flex items-center gap-2">
                                <div className="h-1.5 w-24 overflow-hidden rounded bg-muted">
                                  <div
                                    className="h-full bg-primary transition-all"
                                    style={{ width: `${prog.percent}%` }}
                                  />
                                </div>
                                <span className="text-[11px] text-muted-foreground">
                                  {fmtBytes(prog.received)} / {prog.percent}%
                                </span>
                              </div>
                            ) : (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDownload(file.id, `${file.filename || file.id}`);
                                }}
                                className="gap-1.5"
                                aria-label="Download"
                                title="Download"
                              >
                                <Download className="h-3.5 w-3.5" />
                                Download
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr className="border-b border-border/60 bg-muted/30">
                          <td colSpan={6} className="px-5 py-4">
                            <div className="space-y-4">
                              <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
                                <DetailItem label="ID do Arquivo" value={file.id} mono />
                                <DetailItem label="Nome do Arquivo" value={file.filename} />
                                <DetailItem label="Status" value={info.label} />
                                <DetailItem label="Tamanho" value={fmtBytes(file.bytes)} />
                                <DetailItem label="Propósito" value={file.purpose} />
                                <DetailItem
                                  label="Criado em"
                                  value={secondsToDate(file.created_at)}
                                />
                              </div>
                              {file.status_details && (
                                <div className="space-y-1.5">
                                  <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                                    Detalhes do Status
                                  </p>
                                  <pre className="overflow-x-auto rounded-md border border-border bg-background p-3 font-mono text-xs text-foreground">
                                    {typeof file.status_details === "string"
                                      ? file.status_details
                                      : JSON.stringify(file.status_details, null, 2)}
                                  </pre>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })
              )}
            </tbody>
          </table>
          {!loading && filteredFiles.length === 0 && (
            <div className="sticky left-0 flex h-[325px] w-full items-center justify-center px-5 py-8 text-center">
<div className="mx-auto flex max-w-sm flex-col items-center gap-2">
                      <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-muted text-muted-foreground">
                        <Folder className="h-6 w-6" />
                      </span>
                      <p className="font-semibold text-foreground">Nenhum arquivo encontrado</p>
                      <p className="text-sm text-muted-foreground">
                        Seus arquivos JSONL aparecerão aqui após o primeiro upload.
                      </p>
                    </div>
            </div>
          )}
        </div>

        {hasMore && filteredFiles.length === files.length && (
          <div className="flex justify-center border-t border-border p-4">
            <Button variant="outline" onClick={loadMoreFiles} disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Carregando...
                </>
              ) : (
                "Carregar mais"
              )}
            </Button>
          </div>
        )}
      </Card>

          {!loadingOrganizations && !hasOrganizations && (
            <p className="text-xs text-muted-foreground">
              Crie uma organização para compartilhar arquivos com sua equipe.
            </p>
          )}
          {!loadingOrganizations && hasOrganizations && !hasOrganizationsWithApiAccess && (
            <p className="text-xs text-muted-foreground">
              Você não possui permissões de API em nenhuma organização. Use sua conta pessoal para gerenciar arquivos.
            </p>
          )}
      {/* Upload Modal */}
      <Dialog open={openUploadModal} onOpenChange={(o) => !o && handleCloseModal()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload de Arquivo</DialogTitle>
            <DialogDescription>
              Selecione ou arraste um arquivo JSONL para fazer upload. O arquivo deve ter no
              máximo 200MB e estar no formato correto para processamento em batch.
            </DialogDescription>
          </DialogHeader>

          <div
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            className={cn(
              "flex cursor-pointer flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed p-8 transition-colors",
              selectedFile
                ? "border-primary/50 bg-primary/5"
                : "border-border bg-muted/30 hover:border-foreground/30 hover:bg-muted/50"
            )}
          >
            {selectedFile ? (
              <>
                <div className="flex h-12 w-12 items-center justify-center rounded-md border border-border bg-background text-primary">
                  <Folder className="h-5 w-5" />
                </div>
                <div className="text-center">
                  <p className="font-medium text-foreground">{selectedFile.name}</p>
                  <p className="text-xs text-muted-foreground">{fmtBytes(selectedFile.size)}</p>
                </div>
              </>
            ) : (
              <>
                <div className="flex h-12 w-12 items-center justify-center rounded-md border border-border bg-background text-muted-foreground">
                  <Upload className="h-5 w-5" />
                </div>
                <p className="text-sm text-muted-foreground">
                  Clique aqui ou arraste um arquivo .jsonl
                </p>
              </>
            )}
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept=".jsonl"
            onChange={handleFileSelect}
            style={{ display: "none" }}
          />

          <div className="rounded-md border border-border bg-muted/40 p-3">
            <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Requisitos
            </p>
            <ul className="space-y-0.5 text-[13px] text-foreground/80">
              <li>• Formato: <code className="rounded bg-muted px-1 font-mono text-xs">.jsonl</code> (JSON Lines)</li>
              <li>• Tamanho máximo: 200MB</li>
              <li>• Máximo: 50.000 requisições por arquivo</li>
            </ul>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleCloseModal} disabled={sendingFile}>Cancelar</Button>
            <Button
              onClick={handleUploadFile}
              disabled={sendingFile || !selectedFile}
            >
              {sendingFile ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Enviando...
                </>
              ) : (
                "Fazer Upload"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DetailItem({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div className="space-y-1">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
      <p className={cn("text-[13px] text-foreground", mono && "font-mono")}>{value}</p>
    </div>
  );
}
